/**
 * Skládání reels destinací: pro stránku `/video-inspirace` skupiny po kontinentech
 * (`groupReelsByContinent`, vize svetovy-katalog F4) a pro stránku destinace vlastní
 * a převzaté reels rodiče (`destinationPageReels`, vize web-rijen-2026 F1). Obě jedou přes
 * `videoEntries` z `videoLink.ts`, identita videa pro deduplikaci je tedy jedna.
 *
 * Doménový modul, ne util: potřebuje runtime hodnoty `CONTINENTS`/`CONTINENT_LABELS`
 * z `./types`, zatímco `utils/index.ts` je záměrně bez jediného importu (vstupní bod
 * `@ckgo2/shared/utils` táhnou i klientské bundly). Proto žije top-level vedle
 * `czLabels.ts` / `holidays.ts` / `tripBuckets.ts`.
 *
 * Čistá funkce, aby členění i dedup šly otestovat nezávisle na produkčních datech —
 * ta mají dnes reels jen v Asii a jediný náhodný duplikát, který může kdykoli zmizet
 * redakčním zásahem.
 */
import {
  CONTINENTS,
  CONTINENT_LABELS,
  CONTINENT_ORDER,
  type Continent,
  type MediaResponse,
} from "./types";
import { videoEntries, type VideoTrigger } from "./videoLink";

/** Reel tak, jak přijde z `/api/destinations` (sloupec `socialReels` + populated media). */
export interface RawInspirationReel {
  title?: string | null;
  href?: string | null;
  /** Redakčně zadaná URL náhledu (`SocialReelsEditor`) — druhý stupeň fallbacku. */
  thumbnail?: string | null;
  /** Náhled z media pipeline — první stupeň fallbacku. */
  thumbnailMedia?: MediaResponse | null;
}

export interface RawInspirationDestination {
  name?: string | null;
  continent?: string | null;
  socialReels?: RawInspirationReel[] | null;
}

/** Reel připravený k vykreslení; oba zdroje náhledu nese dál, výběr dělá až renderer. */
export interface InspirationReel {
  title: string;
  /** Spouštěč z klasifikátoru (`videoLink.ts`): kanonická adresa, odkaz, identita videa. */
  trigger: VideoTrigger;
  thumbnail: string | null;
  thumbnailMedia: MediaResponse | null;
  destinationName: string;
}

export interface ContinentReelGroup {
  continent: Continent;
  label: string;
  reels: InspirationReel[];
}

const KNOWN_CONTINENTS: ReadonlySet<string> = new Set(CONTINENTS);

/**
 * Pure: destinace → skupiny reelů po kontinentech.
 *
 * Pravidla: reel přijme klasifikátor odkazu (YouTube i Instagram); vadný i prázdný odkaz
 * se nevykreslí (vadný u pole označí admin, vypíše ho měřidlo `videa.mjs odkazy`). Dedup je
 * GLOBÁLNÍ podle identity videa (napříč destinacemi i poskytovateli — tatáž URL se dvěma
 * `?si=` je jedno video), první výskyt vyhrává; destinace bez kontinentu (nebo s neznámou
 * hodnotou) se přeskočí; pořadí skupin jde z `CONTINENT_ORDER`, popisky z
 * `CONTINENT_LABELS`; prázdné skupiny se nevracejí. Reel bez názvu dědí jméno destinace,
 * aby přístupný název přehrávacího tlačítka nikdy nebyl prázdný.
 */
export function groupReelsByContinent(
  destinations: RawInspirationDestination[],
): ContinentReelGroup[] {
  const candidates = (destinations ?? []).flatMap((destination) => {
    const continent = destination?.continent;
    if (!continent || !KNOWN_CONTINENTS.has(continent)) return [];
    const destinationName = (destination.name ?? "").trim();
    return (destination.socialReels ?? []).map((reel) => ({
      reel,
      continent: continent as Continent,
      destinationName,
    }));
  });

  const byContinent = new Map<Continent, InspirationReel[]>();
  for (const { item, trigger } of videoEntries(candidates, (c) => c.reel?.href, {
    unique: true,
  })) {
    const { reel, continent, destinationName } = item;
    const entry: InspirationReel = {
      title: (reel?.title ?? "").trim() || destinationName,
      trigger,
      thumbnail: (reel?.thumbnail ?? "").trim() || null,
      thumbnailMedia: reel?.thumbnailMedia ?? null,
      destinationName,
    };
    const list = byContinent.get(continent);
    if (list) list.push(entry);
    else byContinent.set(continent, [entry]);
  }

  const groups: ContinentReelGroup[] = [];
  for (const key of CONTINENT_ORDER) {
    const reels = byContinent.get(key);
    if (reels && reels.length > 0) {
      groups.push({ continent: key, label: CONTINENT_LABELS[key], reels });
    }
  }
  return groups;
}

/**
 * Emoji včetně vlajek (regionální indikátory i znaky TAG U+E0020–U+E007F vlajek subdivizí,
 * např. Skotsko), modifikátorů pleti a spojovačů ZWJ/VS16.
 * Alternace po jednom kódovém bodu, ne třída znaků: spojovače ve třídě hlásí ESLint
 * (`no-misleading-character-class`) a odstraňují se stejně po jednom; rozsah TAG třídou být smí
 * (nejsou to spojovače, pravidlo ho nehlásí).
 */
const REEL_TITLE_EMOJI =
  /\p{Extended_Pictographic}|\p{Regional_Indicator}|\p{Emoji_Modifier}|\u{FE0F}|\u{200D}|\u{20E3}|[\u{E0020}-\u{E007F}]/gu;

/**
 * Normalizovaný titul reelu (vize F3): bez emoji a vlajek, interpunkce (`\p{P}`) jako mezera,
 * sloučené mezery, ořezaný, malými písmeny. Symboly mimo emoji (`\p{S}`, např. „+", „€")
 * zůstávají, ať nesplynou tituly lišící se symbolem; diakritika také. Prázdný výsledek se do
 * shody titulů nepočítá (rozhoduje volající).
 */
export function normalizeReelTitle(title: string | null | undefined): string {
  return (title ?? "")
    .replace(REEL_TITLE_EMOJI, "")
    .replace(/\p{P}/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase("cs");
}

/**
 * Strop karet sekce reels stránky destinace (vize F1) — zbytek je na `/video-inspirace`.
 * Jediná konstanta; čte ji výchozí parametr `destinationPageReels`, ne routa.
 */
export const DESTINATION_REELS_CAP = 12;

export interface DestinationPageReels<R> {
  /** Vlastní reels destinace, platné a unikátní, v pořadí uložení. */
  own: R[];
  /** Reels rodiče, které mezi vlastními nejsou, v pořadí uložení u rodiče. */
  inherited: R[];
  /** Po deduplikaci zbyla nad stropem další platná videa → odkaz „Všechna videa“. */
  hasMore: boolean;
}

/**
 * Pure: reels stránky destinace = vlastní, za nimi reels rodiče (vize F1). Kandidáti jdou
 * JEDNÍM průchodem `videoEntries(…, { unique: true })` — první výskyt vyhrává, takže video,
 * které má destinace i rodič, zůstane vlastním; reel s vadným odkazem nebo bez odkazu se
 * vynechá. Druhý průchod sloučí reels se stejným normalizovaným titulem (`normalizeReelTitle`,
 * vize F3; náhled se neporovnává). Strop se řeže až nad deduplikovaným společným seznamem
 * (vlastní mají přednost), `hasMore` počítá videa po obou průchodech nad stropem.
 *
 * O dědění (úroveň destinace, publikace rodiče) rozhoduje volající: kdo dědit nemá, předá
 * `parent = null`. Vrací reely v SUROVÉM tvaru (tytéž objekty, včetně `thumbnailId`).
 */
export function destinationPageReels<R extends RawInspirationReel>(
  own: R[] | null | undefined,
  parent: R[] | null | undefined,
  cap = DESTINATION_REELS_CAP,
): DestinationPageReels<R> {
  const candidates = [
    ...(own ?? []).map((reel) => ({ reel, inherited: false })),
    ...(parent ?? []).map((reel) => ({ reel, inherited: true })),
  ];
  const unique = videoEntries(candidates, (c) => c.reel?.href, { unique: true });
  // Druhý průchod: totéž video pod jinou adresou poznáme podle titulu (vize F3). První výskyt
  // vyhrává (vlastní jsou v kandidátech první); prázdný normalizovaný titul se nikdy neslučuje.
  const seenTitles = new Set<string>();
  const deduped = unique.filter(({ item }) => {
    const key = normalizeReelTitle(item.reel?.title);
    if (!key) return true;
    if (seenTitles.has(key)) return false;
    seenTitles.add(key);
    return true;
  });
  const shown = deduped.slice(0, cap).map(({ item }) => item);
  return {
    own: shown.filter((c) => !c.inherited).map((c) => c.reel),
    inherited: shown.filter((c) => c.inherited).map((c) => c.reel),
    hasMore: deduped.length > cap,
  };
}
